/**
 * Exhibits page composable - matches original script.js exactly
 * Keeps all data and methods in one place to avoid mismatches
 */
import { ref, reactive, computed, onMounted, onBeforeUnmount } from '@vue/composition-api';
import axios from 'axios';
import { getters, mutations } from "@/components/game/store";
import { bannerProps } from "@/components/game/shared/baseBanner";
import eventBus from "@/components/game/store/eventBus";
import { copyContent } from '@/components/share/copy.js';
import { HOST_URL, API_V1, API_V2, API_V3 } from '@/config/api';
import { useTutorial } from '../../composables/tutorial';

export function useExhibitsPage(props) {
  // Props destructuring for template access
  const groupId = props.groupId;

  // Tutorial integration
  const tutorial = useTutorial('exhibits', {
    memberLineId: props.memberLineId,
    groupId: props.groupId
  });

  // Data - matching script.js data()
  const exhibits = ref([]);
  const defaultExhibits = ref([]);
  const foundExhibits = ref([]);
  const selectExhibit = ref({});
  const story_id = ref('');
  const selectExhibitCate = ref('default');
  const found = ref(true);
  const progress = ref([]);
  const totalExhibitNum = ref(0);
  const foundTotalExhibitNum = ref(0);
  const useToolPopup = ref(false);
  const toolNum = ref(0);
  const sendSearchForm = ref(false);
  const hasInitDone = ref(false);

  const icon = reactive({
    search: '',
    exhibitDefault: '',
    exhibitDefaultActive: '',
    exhibitDefaultBG: '',
    exhibitFound: '',
    exhibitFoundActive: '',
    exhibitFoundBg: '',
    tool: '',
    default_exhibit_bg_color: '',
    found_exhibit_bg_color: ''
  });

  const background = reactive({
    mainBg: ''
  });

  const bannerPropsRef = reactive({ ...bannerProps });

  // Computed - matching script.js computed
  const allInitDone = computed(() => hasInitDone.value);

  const mainContentBg = computed(() => {
    return selectExhibitCate.value === 'default' ? icon.exhibitDefaultBG : icon.exhibitFoundBg;
  });

  const computedDefaultStyles = computed(() => {
    const isDefault = selectExhibitCate.value === 'default';
    return {
      backgroundColor: isDefault && foundExhibits.value.length > 0 ? icon.found_exhibit_bg_color : ''
    };
  });

  const computedFoundStyles = computed(() => {
    const isDefault = selectExhibitCate.value === 'default';
    return {
      backgroundColor: !isDefault ? icon.default_exhibit_bg_color : ''
    };
  });

  // Methods - matching script.js methods
  const initViewData = () => {
    const topicInfo = getters.getTopicInfo('exhibit');
    bannerPropsRef.imgSrc = topicInfo.banner;
    bannerPropsRef.content = sampleContent(topicInfo.banner_content);
    bannerPropsRef.banner_avatar = topicInfo.banner_avatar;
    bannerPropsRef.styles = "top: 50px; left: 155px;";
    icon.search = topicInfo.tool;
    icon.exhibitDefault = topicInfo.default_exhibit;
    icon.exhibitDefaultActive = topicInfo.default_exhibit_active;
    icon.exhibitDefaultBG = topicInfo.default_exhibit_background;
    icon.exhibitFound = topicInfo.found_exhibit;
    icon.exhibitFoundActive = topicInfo.found_exhibit_active;
    icon.exhibitFoundBg = topicInfo.found_exhibit_background;
    icon.tool = topicInfo.sum;
    icon.default_exhibit_bg_color = topicInfo.default_exhibit_bg_color;
    icon.found_exhibit_bg_color = topicInfo.found_exhibit_bg_color;
    background.mainBg = topicInfo.background;
  };

  const init = (data) => {
    story_id.value = data.group.story_id;
    progress.value = data.progress;
    exhibits.value = data.exhibits;
    defaultExhibits.value = data.exhibits.filter(exhibit => exhibit.status === 'default');
    foundExhibits.value = data.exhibits
      .filter(exhibit => exhibit.status === 'found' && data.progress.includes(exhibit.id))
      .sort((a, b) => progress.value.indexOf(a.id) - progress.value.indexOf(b.id));
    selectExhibit.value = defaultExhibits.value[0] || {};
    totalExhibitNum.value = data.exhibits.length;
    foundTotalExhibitNum.value = data.progress.length;
    if (data.demo && props.updateFinishedDemo) {
      props.updateFinishedDemo();
    }
  };

  const getToolNum = () => {
    return axios.get(`${HOST_URL}${API_V1}/line/get_tool_num?line_id=${props.memberLineId}`)
      .then(response => { setToolNum(response.data.tool_num); })
      .catch(error => { console.log(error); });
  };

  const setToolNum = (num) => {
    toolNum.value = num || 0;
  };

  const userTool = async () => {
    await getToolNum();
    useToolPopup.value = true;
  };

  const searchExhibit = (queryText) => {
    if (sendSearchForm.value) { return; }

    sendSearchForm.value = true;
    let targetExhibit = exhibits.value.find(
      exhibit => exhibit.name === queryText || exhibit.keywords.includes(queryText)
    );

    if ((targetExhibit && progress.value.includes(targetExhibit.id)) ||
        (targetExhibit && targetExhibit.status === 'default')) {
      selectExhibit.value = targetExhibit;
      selectExhibitCate.value = targetExhibit.status;
      sendSearchForm.value = false;
      // Auto-advance tutorial if waiting for search action
      advanceTutorialOnSearch(queryText);
      return;
    }

    if (props.updateGroupStoryShip) {
      props.updateGroupStoryShip('exhibit', queryText);
    }
    sendSearchForm.value = false;
    if (!targetExhibit) {
      // Auto-advance tutorial even if not found
      advanceTutorialOnSearch(queryText);
      return;
    }

    updatedFoundList(targetExhibit);
    selectExhibit.value = targetExhibit;
    selectExhibitCate.value = 'found';
    if (foundTotalExhibitNum.value === totalExhibitNum.value) {
      window.liff.sendMessages([{ type: 'text', text: "我發現全部證物了！！" }]);
    }
    // Auto-advance tutorial if waiting for search action
    advanceTutorialOnSearch(queryText);
  };

  // Helper to advance tutorial when search action is completed
  const advanceTutorialOnSearch = (queryText) => {
    if (tutorial.isActive.value &&
        tutorial.currentStep.value &&
        tutorial.currentStep.value.waitForAction === 'search') {
      // Validate that user typed the correct search term
      const expectedTerm = '住院紀錄';
      if (queryText !== expectedTerm) {
        alert(`請輸入「${expectedTerm}」來繼續教學！`);
        return;
      }
      // Small delay to let UI update first
      setTimeout(() => {
        tutorial.nextStep();
      }, 300);
    }
  };

  const updatedFoundList = (newItem) => {
    if (progress.value.includes(newItem.id)) { return; }

    progress.value.push(newItem.id);
    foundExhibits.value.push(newItem);
    foundTotalExhibitNum.value += 1;
  };

  const updateReceivedHadFoundItem = (itemType, item) => {
    if (!item) { return; }
    if (itemType !== 'hadFoundExhibitIds') { return; }

    updatedFoundList(item);
  };

  const changeLine = (content) => {
    if (!content) { return ''; }
    return content.replaceAll('\n', '<br>');
  };

  const sampleContent = (contents) => {
    if (!contents || !contents.length) return '';
    return contents[Math.floor(Math.random() * contents.length)];
  };

  const setSelectExhibit = (cate, tag) => {
    found.value = true;
    selectExhibitCate.value = cate;
    if (cate === 'default') {
      selectExhibit.value = defaultExhibits.value.find(exhibit => exhibit.tag === tag);
    } else {
      selectExhibit.value = foundExhibits.value.find(exhibit => exhibit.tag === tag);
    }
  };

  const sharedOwnExhibits = async () => {
    if (foundExhibits.value.length === 0) {
      alert('哎呀！你沒找到任何證物，別開我玩笑了！');
    } else {
      const foundExhibitNames = foundExhibits.value.map(e => e.name);
      let copyText = `嗨，判官\n以下是魅嵐幫你整理好的證物：\n${foundExhibitNames.join(', ')}`;
      if ((foundExhibitNames.length + defaultExhibits.value.length) < exhibits.value.length) {
        copyText += `\n\n本案尚有： ${exhibits.value.length - defaultExhibits.value.length - foundExhibitNames.length} 個證物未被找到`;
      }
      copyContent(copyText, '複製成功，快去分享證物吧～');
    }
  };

  const accept = async () => {
    try {
      const response = (await axios.post(
        `${HOST_URL}${API_V3}/stories/${story_id.value}/unlock_exhibits`,
        { groupId: props.groupId, memberLineId: props.memberLineId }
      )).data;
      console.log(response);
      if (response.success) {
        toolNum.value = response.cakeNumber;
        useToolPopup.value = false;
        searchExhibit(response.exhibitName);
      }
    } catch (e) {
      alert(e);
    }
  };

  const onLoaded = () => {
    hasInitDone.value = true;
  };

  // Watch for loading state
  // eslint-disable-next-line no-unused-vars
  const unwatchLoading = computed(() => {
    if (allInitDone.value) {
      mutations.closeLoading();
    } else {
      mutations.openLoading();
    }
    return allInitDone.value;
  });

  // Lifecycle - matching script.js created/mounted/beforeDestroy
  onMounted(async () => {
    initViewData();
    try {
      const response = await axios.get(
        `${HOST_URL}${API_V2}/groups/${props.groupId || props.memberLineId}/exhibits?line_id=${props.memberLineId}`
      );
      init(response.data);
      await getToolNum();

      // Initialize tutorial from API response
      tutorial.initFromApiResponse(response.data);
    } catch (error) {
      console.log(`遊戲服務中斷: ${error.message}`);
    }

    // Event bus subscription
    eventBus.$on('received', updateReceivedHadFoundItem);
  });

  onBeforeUnmount(() => {
    eventBus.$off('received', updateReceivedHadFoundItem);
  });

  // Return everything the template needs - matching script.js exactly
  return {
    // Props (needed in template)
    groupId,

    // Data
    exhibits,
    defaultExhibits,
    foundExhibits,
    selectExhibit,
    story_id,
    selectExhibitCate,
    found,
    progress,
    totalExhibitNum,
    foundTotalExhibitNum,
    useToolPopup,
    toolNum,
    sendSearchForm,
    hasInitDone,
    icon,
    background,
    bannerProps: bannerPropsRef,

    // Computed
    allInitDone,
    mainContentBg,
    computedDefaultStyles,
    computedFoundStyles,

    // Methods
    initViewData,
    init,
    getToolNum,
    setToolNum,
    userTool,
    searchExhibit,
    updatedFoundList,
    updateReceivedHadFoundItem,
    changeLine,
    sampleContent,
    setSelectExhibit,
    sharedOwnExhibits,
    accept,
    onLoaded,

    // Tutorial
    tutorial
  };
}
